/**
 * Cart drawer order-summary card.
 *
 * Default (Figma 6:157) state is a READ-ONLY recap built from cart
 * attributes that product-delivery-selector.js already writes on the
 * product page: Delivery-Address / Delivery-Date / Delivery-Time /
 * Estimated-Delivery-Rate / Checkout-Method, or the Pickup-Location-* /
 * Pickup-Date / Pickup-Time equivalents.
 *
 * "Change" (Figma 8:219) swaps in the same controls the product page
 * uses — shipping-method pills, address+cost row (Google Places +
 * distance-based pricing), date (Flatpickr) + time (Zapiet slots) —
 * driven by the live cart instead of a single product's add-to-cart
 * form. Every save uses the exact same cart attribute keys
 * product-delivery-selector.js uses, so either page can update them and
 * both stay in sync.
 */

(function () {
  const ROOT_SELECTOR = '[data-cart-delivery-calculator]';
  const SESSION_KEY = 'luciol_cart_delivery_address';

  async function loadGoogleMapsReady() {
    if (window.google?.maps) return;
    await new Promise((resolve) => {
      const check = () => (window.google?.maps ? resolve() : setTimeout(check, 100));
      check();
    });
  }

  function computeDistanceMiles(storeAddress, destination) {
    return new Promise((resolve, reject) => {
      if (!window.google?.maps?.DistanceMatrixService) {
        reject(new Error('Google Maps DistanceMatrixService unavailable'));
        return;
      }
      const service = new google.maps.DistanceMatrixService();
      service.getDistanceMatrix(
        {
          origins: [storeAddress],
          destinations: [destination],
          travelMode: 'DRIVING',
          unitSystem: google.maps.UnitSystem.IMPERIAL,
        },
        (response, status) => {
          if (status !== 'OK') return reject(new Error('Distance Matrix request failed'));
          const element = response.rows?.[0]?.elements?.[0];
          if (!element || element.status !== 'OK') return reject(new Error('No route found'));
          resolve({ miles: element.distance.value / 1609.34 });
        }
      );
    });
  }

  // Turns a plain address string into structured components
  // (address1/city/province/zip/country) via Google's Geocoder. Needed
  // because lastAddressComponents used to only ever get set inside the
  // Places Autocomplete's own place_changed listener — any address that
  // reached validateDeliveryAddress() a different way (Enter key, the
  // blur fallback, or a staff-sent ?prefill_address= link, which calls
  // validateDeliveryAddress() directly with a plain string and never
  // touches the autocomplete widget at all) left Delivery-Address1/City/
  // Province/Zip/Country blank, which is what the checkout-button script
  // in cart-drawer.liquid reads for its cart-attribute fallback. Resolves
  // to null (never rejects) so a geocoding hiccup doesn't block checking
  // the address's delivery eligibility, which is the more critical half.
  function geocodeAddress(query) {
    return new Promise((resolve) => {
      if (!window.google?.maps?.Geocoder) {
        resolve(null);
        return;
      }
      new google.maps.Geocoder().geocode({ address: query }, (results, status) => {
        if (status !== 'OK' || !results?.[0]?.address_components) {
          resolve(null);
          return;
        }
        resolve(extractAddressComponents(results[0].address_components));
      });
    });
  }

  // cart.items_subtotal_price already includes the hidden "Home
  // Delivery" fee line item once syncDeliveryFeeItem() has added it (see
  // that function's own comment for why it's a real line item at all) —
  // Shopify has no concept of a "hidden" line, it's just another item to
  // Shopify's own subtotal math. Using the raw value anywhere customer-
  // facing double-counts the delivery fee (once baked into the
  // subtotal, once shown again as its own "Delivery" row), and using it
  // for the free-delivery-threshold check below can make a near-
  // threshold order look bigger than it really is. This strips the
  // hidden line back out everywhere the "real" order subtotal matters.
  function getVisibleSubtotalCents(cart) {
    const hiddenCents = (cart.items || [])
      .filter((item) => item.properties && item.properties._hidden_fee)
      .reduce((sum, item) => sum + item.line_price, 0);
    return (cart.items_subtotal_price || 0) - hiddenCents;
  }

  // Flat-fee distance tiers (see window.LUCIOL_DELIVERY_RATES in
  // product-delivery-selector.js for the tier table itself) with
  // free-delivery rules (order subtotal + distance) checked first.
  function resolveRateByMiles(miles, subtotalDollars) {
    const config = window.LUCIOL_DELIVERY_RATES || {};
    const tiers = config.tiers || [];
    const maxMiles = Number(config.maxMiles ?? Infinity);
    const freeRules = config.freeDeliveryRules || [];

    if (miles > maxMiles) return { miles, eligible: false };

    const subtotal = Number(subtotalDollars) || 0;
    const isFree = freeRules.some((rule) => subtotal >= rule.minSubtotal && miles <= rule.withinMiles);
    if (isFree) return { miles, price: 0, eligible: true };

    const tier = tiers.find((t) => miles <= t.upTo);
    if (!tier) return { miles, eligible: false };

    return { miles, price: tier.price, eligible: true };
  }

  function money(cents, currency) {
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(cents / 100);
    } catch {
      return `$${(cents / 100).toFixed(2)}`;
    }
  }

  function formatDisplayTime(t) {
    const [h, m] = t.split(':');
    const n = Number(h);
    return `${n % 12 || 12}:${m} ${n >= 12 ? 'PM' : 'AM'}`;
  }

  function formatCartDate(d) {
    return d.replaceAll('-', '/');
  }

  // Picks the first real (non-placeholder) option in a <select> of time
  // slots and returns whether one was found. Used to auto-select a slot
  // the moment it's populated, instead of leaving the dropdown on
  // "Choose a time" until the customer manually opens it — see the
  // _ZapietId comment further down for why that matters.
  function selectFirstAvailableOption(selectEl) {
    if (!selectEl) return false;
    const firstReal = Array.from(selectEl.options).find((o) => o.value);
    if (!firstReal) return false;
    selectEl.value = firstReal.value;
    return true;
  }

  // Same format Zapiet's own checkout script expects, and the same
  // builder product-delivery-selector.js uses on the product page:
  // "M=D&L=<locationId>&D=<date>T<start>:00Z&E=<end>" (M=P for pickup).
  function buildZapietId(m, l, d, s, e) {
    return `M=${m}&L=${l}&D=${d}T${s}:00Z&E=${e}`;
  }

  // `id` here is the line item's unique `key` (e.g.
  // "45235184402479:b64c..."), not a bare variant ID — cart/change.js
  // accepts either, but a variant ID would target ALL lines sharing that
  // variant (e.g. two different Card-message notes on the same product),
  // silently merging/overwriting the wrong one's properties.
  async function changeLineItem(id, quantity, properties) {
    const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id, quantity, properties }),
    });
    if (!res.ok) throw new Error('Cart line update failed');
    return res.json();
  }

  /**
   * Every line item's `_ZapietId` property is baked in at add-to-cart
   * time by product-delivery-selector.js. cart/update.js's `attributes`
   * payload only ever touches CART-level attributes — it can't change a
   * line item's properties — so switching method/date/time here in the
   * cart drawer was updating our own Checkout-Method/recap attributes
   * (which our UI reads) while every line item silently kept its old
   * _ZapietId from whenever it was added. Zapiet's own checkout script
   * reads that per-line property, not our cart attributes, so checkout
   * kept showing whatever method was selected when the item was first
   * added. This pushes the freshly-built zapietId onto every line so
   * checkout actually reflects what was just chosen in the cart.
   */
  async function syncLineItemsZapietId(zapietId, extraProperties) {
    const cart = await getCart();
    for (let i = 0; i < cart.items.length; i++) {
      const item = cart.items[i];
      const properties = { ...(item.properties || {}) };
      properties['_ZapietId'] = zapietId;
      if (extraProperties) Object.assign(properties, extraProperties);
      try {
        // Was `line: i+1` (positional) — a customer adding/removing an
        // item (a second "Add to Cart" from another tab, a quantity
        // change) between this getCart() snapshot and this specific
        // await meant position i+1 could silently point at a DIFFERENT
        // line than the one just read, especially mid-loop across
        // several sequential awaits. `key` addresses the exact line
        // item regardless of what else changes around it — same
        // approach product-delivery-selector.js's syncDeliveryFeeItem()
        // already uses.
        await changeLineItem(item.key, item.quantity, properties);
      } catch (err) {
        console.error('[CartDeliveryCalculator] Failed to sync _ZapietId for line', item.key, err);
      }
    }
  }

  // Zapiet's calendar API returns `disabled` as an array of
  // [year, month, day] tuples with a ZERO-INDEXED month (e.g. [2026, 9, 7]
  // for October 7, 2026 — confirmed by calling the API directly), NOT
  // "YYYY-MM-DD" strings. Both flatpickr's `disable` option and our own
  // `.includes(dateInput.value)` checks need real date strings, so every
  // consumer of `data.disabled` must run it through this first.
  function normalizeDisabledDates(raw) {
    if (!Array.isArray(raw)) return [];
    return raw.map((entry) => {
      if (Array.isArray(entry) && entry.length >= 3) {
        const [y, m, d] = entry;
        return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      }
      return entry;
    });
  }

  // Groups Zapiet's flat `slots` array (each entry: { day_of_week,
  // available_from, available_until, id, ... }, one entry per window per
  // weekday) into the { monday: [...], tuesday: [...], ... } shape the
  // rest of this file already expects. Confirmed via a direct API call
  // that Zapiet's delivery calendar endpoint returns this for the WHOLE
  // week, not just the "nearest available" date — so this replaces the
  // guessed defaultDeliverySchedule() below with the real, live schedule
  // whenever the request succeeds. Only a total fetch failure (network
  // error, Zapiet outage) should ever fall back to the hardcoded copy.
  function groupSlotsByWeekday(slots) {
    const result = {};
    slots.forEach((s) => {
      const day = s.day_of_week;
      if (!day || !s.available_from || !s.available_until) return;
      if (!result[day]) result[day] = [];
      result[day].push({ id: String(s.id || ''), from: s.available_from, until: s.available_until });
    });
    return result;
  }

  // Zapiet's pickup calendar doesn't give a flat slot list like delivery
  // does — instead `daysOfWeek` gives a single { min: {hour, minute},
  // max: {hour, minute} } open/close window per weekday, and individual
  // slots are implied to be hourly within it (confirmed against a real
  // minDateSlots response, which steps hour-by-hour). This expands that
  // min/max window into the same hourly-slot shape
  // defaultPickupSchedule() below already uses.
  function buildPickupScheduleFromDaysOfWeek(daysOfWeek) {
    if (!daysOfWeek || typeof daysOfWeek !== 'object') return null;
    const result = {};
    Object.keys(daysOfWeek).forEach((day) => {
      const range = daysOfWeek[day];
      const startH = Number(range?.min?.hour);
      const startM = Number(range?.min?.minute || 0);
      const endH = Number(range?.max?.hour);
      const endM = Number(range?.max?.minute || 0);
      if (!Number.isFinite(startH) || !Number.isFinite(endH)) return;
      const slots = [];
      let h = startH;
      let m = startM;
      let idx = 0;
      while (h * 60 + m < endH * 60 + endM) {
        const from = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
        let nh = h + 1;
        let nm = m;
        if (nh * 60 + nm > endH * 60 + endM) { nh = endH; nm = endM; }
        const until = `${String(nh).padStart(2, '0')}:${String(nm).padStart(2, '0')}`;
        slots.push({ id: `pickup-${day}-${idx}`, from, until });
        h = nh; m = nm; idx += 1;
      }
      result[day] = slots;
    });
    return result;
  }

  // Last-resort fallback if the Zapiet calendar request fails outright
  // (network error, API outage) — kept roughly in sync with the real
  // schedule but NOT the source of truth once groupSlotsByWeekday() above
  // can run.
  function defaultDeliverySchedule() {
    const slots = [
      { id: '139607615', from: '09:00', until: '15:00' },
      { id: '139607616', from: '11:00', until: '17:00' },
      { id: '139607617', from: '15:00', until: '20:00' },
      { id: '139607619', from: '18:00', until: '21:00' },
      { id: '139607618', from: '21:00', until: '23:45' },
    ];
    return {
      monday: slots, tuesday: slots, wednesday: slots, thursday: slots,
      friday: slots, saturday: slots, sunday: slots, disabled: [],
    };
  }

  // Pickup is a single moment ("come by at 2pm"), not a window like
  // delivery ("sometime between 3 and 9") — the fallback schedule used
  // for any date beyond Zapiet's real minDateSlots (see
  // renderPickupSlotsForDate()) needs its own on-the-hour slots instead
  // of reusing defaultDeliverySchedule()'s ranges.
  // LAST-RESORT fallback only — a direct API call showed Zapiet's real
  // pickup hours are 07:00-23:00 every day (via `daysOfWeek`), not this
  // 9:30-21:00 range. buildPickupScheduleFromDaysOfWeek() above replaces
  // this with the real hours whenever the calendar request succeeds;
  // this hand-kept copy only matters if that request fails outright.
  // Mirrors product-delivery-selector.js's defaultPickupSchedule().
  function defaultPickupSchedule() {
    const hours = [
      { id: 'pickup-hour-0930', from: '09:30', until: '10:00' },
      { id: 'pickup-hour-10', from: '10:00', until: '11:00' },
      { id: 'pickup-hour-11', from: '11:00', until: '12:00' },
      { id: 'pickup-hour-12', from: '12:00', until: '13:00' },
      { id: 'pickup-hour-13', from: '13:00', until: '14:00' },
      { id: 'pickup-hour-14', from: '14:00', until: '15:00' },
      { id: 'pickup-hour-15', from: '15:00', until: '16:00' },
      { id: 'pickup-hour-16', from: '16:00', until: '17:00' },
      { id: 'pickup-hour-17', from: '17:00', until: '18:00' },
      { id: 'pickup-hour-18', from: '18:00', until: '19:00' },
      { id: 'pickup-hour-19', from: '19:00', until: '20:00' },
      { id: 'pickup-hour-20', from: '20:00', until: '21:00' },
    ];
    return {
      monday: hours, tuesday: hours, wednesday: hours, thursday: hours,
      friday: hours, saturday: hours, sunday: hours, disabled: [],
    };
  }

  // "Now", as wall-clock date + minutes-since-midnight in the store's
  // own timezone — independent of the visitor's device/browser
  // timezone. Mirrors getStoreNow() in product-delivery-selector.js.
  function getStoreNow(timeZone) {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false,
    }).formatToParts(new Date());
    const map = {};
    parts.forEach((p) => { map[p.type] = p.value; });
    let hour = Number(map.hour);
    if (hour === 24) hour = 0;
    return {
      dateStr: `${map.year}-${map.month}-${map.day}`,
      minutes: hour * 60 + Number(map.minute),
    };
  }

  // True only when dateStr (Y-m-d) is today in the STORE's timezone —
  // deliberately not the visitor's device/browser timezone. Mirrors
  // product-delivery-selector.js's isDateToday().
  function isDateToday(dateStr) {
    if (!dateStr) return false;
    const timeZone = window.LUCIOL_LEAD_TIME?.storeTimeZone || 'America/Los_Angeles';
    return dateStr === getStoreNow(timeZone).dateStr;
  }

  // Drops any slot starting at or before "now + leadTimeHours" (store
  // time) when the selected date is today — same rule as
  // product-delivery-selector.js (kept duplicated rather than shared,
  // matching how the rest of this file already duplicates its logic
  // instead of importing it).
  function filterSlotsByLeadTime(slots, dateStr, leadTimeHours, fromKey = 'from') {
    if (!isDateToday(dateStr)) return slots;
    const timeZone = window.LUCIOL_LEAD_TIME?.storeTimeZone || 'America/Los_Angeles';
    const cutoffMinutes = getStoreNow(timeZone).minutes + leadTimeHours * 60;
    return slots.filter((slot) => {
      const time = slot[fromKey];
      if (!time) return true;
      const [h, m] = time.split(':').map(Number);
      return h * 60 + m > cutoffMinutes;
    });
  }

  function extractAddressComponents(components) {
    if (!Array.isArray(components)) return null;
    const get = (type, useShort) => {
      const match = components.find((c) => c.types?.includes(type));
      return match ? (useShort ? match.short_name : match.long_name) : '';
    };
    const streetNumber = get('street_number');
    const route = get('route');
    return {
      address1: [streetNumber, route].filter(Boolean).join(' '),
      city: get('locality') || get('postal_town') || get('sublocality'),
      province: get('administrative_area_level_1', true),
      zip: get('postal_code'),
      country: get('country', true),
    };
  }

  async function saveCartAttributes(attributes) {
    const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes }),
    });
    if (!res.ok) throw new Error('Cart update failed');
    return res.json();
  }

  async function getCart() {
    const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`);
    return res.json();
  }

  // Every attribute this component ever writes onto the cart. Cleared
  // once the cart is empty (see refreshBreakdown) so a saved address
  // from a previous order doesn't silently reappear on the next one —
  // Shopify carts otherwise persist for weeks regardless of session.
  const DELIVERY_ATTR_KEYS = [
    'Checkout-Method',
    'Delivery-Location-Id',
    'Delivery-Date',
    'Delivery-Time',
    'Delivery-Slot-Id',
    'Delivery-Address',
    'Delivery-Widget-Query',
    'Delivery-Miles',
    'Delivery-Address1',
    'Delivery-City',
    'Delivery-Province',
    'Delivery-Zip',
    'Delivery-Country',
    'Estimated-Delivery-Rate',
    'Pickup-Location-Id',
    'Pickup-Date',
    'Pickup-Time',
    'Pickup-Location-Company',
    'Pickup-Location-Address-Line-1',
    'Pickup-Location-Address-Line-2',
    'Pickup-Location-City',
    'Pickup-Location-Region',
    'Pickup-Location-Postal-Code',
    'Pickup-Location-Country',
    'Pickup-Location-Phone',
  ];

  function hasAnyDeliveryAttribute(attributes) {
    return DELIVERY_ATTR_KEYS.some((key) => attributes?.[key]);
  }

  async function clearDeliveryAttributes() {
    const cleared = {};
    DELIVERY_ATTR_KEYS.forEach((key) => { cleared[key] = ''; });
    await saveCartAttributes(cleared);
  }

  function saveToSession(address, components) {
    try {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify({ address, components }));
    } catch (err) {
      console.info('[CartDeliveryCalculator] sessionStorage unavailable.', err);
    }
  }

  function readFromSession() {
    try {
      const raw = sessionStorage.getItem(SESSION_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  async function init(root) {
    const storeAddress = root.dataset.storeAddress;
    const deliveryLocationId = root.dataset.deliveryLocationId || '300262';
    const shopDomain = window.Shopify?.shop || '';

    // Breakdown / recap
    const subtotalEl = root.querySelector('[data-cart-items-subtotal]');
    const priceEl = root.querySelector('[data-cart-delivery-price]');
    const deliveryLabelEl = root.querySelector('[data-cart-delivery-label]');
    const orderTotalEl = root.querySelector('[data-cart-order-total]');
    const priceRow = root.querySelector('[data-cart-delivery-price-row]');
    const recapAddressEl = root.querySelector('[data-cart-recap-address]');
    const recapDatetimeEl = root.querySelector('[data-cart-recap-datetime]');
    const pickupRecapNoteEl = root.querySelector('[data-cart-pickup-recap-note]');
    const recapRow = root.querySelector('[data-cart-recap-row]');
    const changeBtn = root.querySelector('[data-cart-change-delivery]');

    // Edit panel
    const editPanel = root.querySelector('[data-cart-edit-panel]');
    const backBtn = root.querySelector('[data-cart-change-back]');
    const methodButtons = root.querySelectorAll('[data-cart-method]');
    const deliveryFieldsPanel = root.querySelector('[data-cart-method-panel="delivery"]');
    const pickupFieldsPanel = root.querySelector('[data-cart-method-panel="pickup"]');
    const addressInput = root.querySelector('[data-cart-delivery-address]');
    const editPriceEl = root.querySelector('[data-cart-edit-delivery-price]');
    const dateInput = root.querySelector('[data-cart-delivery-date]');
    const timeSelect = root.querySelector('[data-cart-delivery-time]');
    const errorEl = root.querySelector('[data-cart-delivery-error]');
    const pickupDateInput = root.querySelector('[data-cart-pickup-date]');
    const pickupTimeSelect = root.querySelector('[data-cart-pickup-time]');
    const pickupLineEl = root.querySelector('[data-cart-pickup-line]');

    let method = 'delivery';
    let lastAddressComponents = null;
    let lastRate = null;
    let editPanelReady = false;
    // The last address text that actually finished validating (got a
    // real rate + saved cart attributes) — used to tell "customer is
    // still typing/hasn't confirmed anything new" apart from "this is
    // the same address that's already validated, nothing to redo".
    let lastValidatedAddress = '';
    let addressInvalidateTimer = null;
    const deliverySchedule = defaultDeliverySchedule();
    const pickupHourSchedule = defaultPickupSchedule();
    let pickupLocations = [];
    let pickupCalendar = null;
    // Tracks whether initializeDeliveryCalendar()'s Zapiet round trip has
    // run yet — the edit panel now only loads whichever method is
    // currently active up front (see changeBtn below), so switching to
    // Delivery afterwards needs to fetch it lazily instead of assuming
    // it's already there.
    let deliveryCalendarLoaded = false;

    async function getShoppingCartItems(cart) {
      const source = cart || (await getCart());
      return (source.items || []).map((i) => ({
        variant_id: i.variant_id,
        product_id: i.product_id || null,
        sku: i.sku || null,
        quantity: i.quantity,
      }));
    }

    function currentCurrency(cart) {
      return cart?.currency || window.LUCIOL_DELIVERY_RATES?.currency || 'USD';
    }

    // ---------------------------------------------------------------
    // Recap rendering (default state)
    // ---------------------------------------------------------------
    function renderRecap(attributes) {
      const isPickup = attributes['Checkout-Method'] === 'pickup';
      method = isPickup ? 'pickup' : 'delivery';
      if (pickupRecapNoteEl) pickupRecapNoteEl.hidden = !isPickup;

      if (isPickup) {
        if (deliveryLabelEl) deliveryLabelEl.textContent = 'Pickup';
        const pickupParts = [
          attributes['Pickup-Location-Company'],
          attributes['Pickup-Location-Address-Line-1'],
        ].filter(Boolean);
        if (recapAddressEl) recapAddressEl.textContent = pickupParts.join(', ') || storeAddress;
        if (recapDatetimeEl) {
          recapDatetimeEl.textContent = [attributes['Pickup-Date'], attributes['Pickup-Time']]
            .filter(Boolean)
            .join(', ');
        }
      } else {
        if (deliveryLabelEl) deliveryLabelEl.textContent = 'Delivery';
        if (recapAddressEl) recapAddressEl.textContent = attributes['Delivery-Address'] || '';
        if (recapDatetimeEl) {
          recapDatetimeEl.textContent = [attributes['Delivery-Date'], attributes['Delivery-Time']]
            .filter(Boolean)
            .join(', ');
        }
      }

      methodButtons.forEach((b) => {
        const active = b.dataset.cartMethod === method;
        b.classList.toggle('is-active', active);
        b.setAttribute('aria-pressed', String(active));
      });
      if (deliveryFieldsPanel) deliveryFieldsPanel.hidden = method !== 'delivery';
      if (pickupFieldsPanel) pickupFieldsPanel.hidden = method !== 'pickup';

      // `> 0` used to treat a genuinely free $0 rate (see the distance-
      // tier free-delivery rules) the same as "no address entered yet" —
      // both read as falsy. Checking the raw attribute's presence instead
      // of the parsed number distinguishes "not calculated" from "$0".
      const rateRaw = attributes['Estimated-Delivery-Rate'];
      const hasRate = !isPickup && rateRaw !== undefined && rateRaw !== '' && !Number.isNaN(Number(rateRaw));
      lastRate = hasRate ? { price: Number(rateRaw), eligible: true } : lastRate;
    }

    async function refreshBreakdown() {
      let cart = await getCart();
      if (cart.item_count === 0 && hasAnyDeliveryAttribute(cart.attributes)) {
        await clearDeliveryAttributes();
        cart = await getCart();
      }
      const currency = currentCurrency(cart);
      const attributes = cart.attributes || {};
      renderRecap(attributes);

      const visibleSubtotalCents = getVisibleSubtotalCents(cart);
      if (subtotalEl) subtotalEl.textContent = money(visibleSubtotalCents, currency);

      const isPickup = attributes['Checkout-Method'] === 'pickup';
      // Same "presence, not truthiness" fix as renderRecap() above — a
      // free $0 delivery rate must still count as "calculated".
      const rateRaw = attributes['Estimated-Delivery-Rate'];
      const hasRate = rateRaw !== undefined && rateRaw !== '' && !Number.isNaN(Number(rateRaw));
      const rate = hasRate ? Number(rateRaw) : null;

      if (isPickup) {
        if (priceEl) priceEl.textContent = 'Free';
        if (orderTotalEl) orderTotalEl.textContent = money(visibleSubtotalCents, currency);
        if (priceRow) priceRow.hidden = false;
      } else if (rate !== null) {
        const deliveryCents = Math.round(rate * 100);
        if (priceEl) priceEl.textContent = deliveryCents === 0 ? 'Free' : money(deliveryCents, currency);
        if (orderTotalEl) orderTotalEl.textContent = money(visibleSubtotalCents + deliveryCents, currency);
        if (priceRow) priceRow.hidden = false;
      } else {
        if (priceEl) priceEl.textContent = 'Enter address above';
        if (orderTotalEl) orderTotalEl.textContent = money(visibleSubtotalCents, currency);
      }

      if (editPriceEl) editPriceEl.textContent = priceEl ? priceEl.textContent : '—';
      return cart;
    }

    // ---------------------------------------------------------------
    // Flatpickr — same brand-styled calendar as the product page,
    // dropped onto the cart's Date / Pickup date inputs.
    // ---------------------------------------------------------------
    function initDatePickers() {
      if (!window.flatpickr) return;
      const shared = {
        altInput: true,
        altInputClass: 'cart-summary__field-control flatpickr-alt-input',
        altFormat: 'd / m / Y',
        dateFormat: 'Y-m-d',
        minDate: 'today',
        disableMobile: true,
      };
      if (dateInput && !dateInput._flatpickr) flatpickr(dateInput, shared);
      if (pickupDateInput && !pickupDateInput._flatpickr) flatpickr(pickupDateInput, shared);
    }

    // ---------------------------------------------------------------
    // Delivery: Zapiet calendar + Google Places + distance pricing
    // ---------------------------------------------------------------
    async function initializeDeliveryCalendar() {
      try {
        const url = new URL(`https://api.zapiet.com/v1.0/delivery/locations/${deliveryLocationId}/calendar`);
        url.searchParams.set('shop', shopDomain);
        const shoppingCart = await getShoppingCartItems();
        const res = await fetch(url.toString(), {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json; charset=UTF-8' },
          body: JSON.stringify({ shoppingCart }),
        });
        if (!res.ok) return;
        const data = await res.json();
        // Zapiet's own timezone for this location — confirmed via a
        // direct API call to be 'America/Boise', NOT the
        // 'America/Los_Angeles' this file and product-delivery-selector.js
        // used to hardcode. That 1-hour gap shifted the lead-time cutoff
        // (see getStoreNow()/filterSlotsByLeadTime() below) an hour
        // early, which is what made the last delivery window quietly
        // disappear 2-3 hours before actual closing instead of ~1 hour
        // before it. Overwriting the shared global here means every page
        // that reads window.LUCIOL_LEAD_TIME picks up the real value the
        // moment either calendar request resolves.
        if (data.timezone) {
          window.LUCIOL_LEAD_TIME = window.LUCIOL_LEAD_TIME || {};
          window.LUCIOL_LEAD_TIME.storeTimeZone = data.timezone;
        }
        // Zapiet's calendar response includes the FULL weekly schedule
        // under `slots` (every day_of_week, not just the nearest
        // available date) — confirmed via a direct API call. This is the
        // real, live source of truth; defaultDeliverySchedule()'s
        // hand-kept copy only matters if this request fails outright.
        if (Array.isArray(data.slots) && data.slots.length) {
          Object.assign(deliverySchedule, groupSlotsByWeekday(data.slots));
        }
        if (data.minDate && dateInput) {
          dateInput.min = data.minDate;
          if (dateInput._flatpickr) dateInput._flatpickr.set('minDate', data.minDate);
          // Used to also do `dateInput.value = data.minDate` here so
          // there'd always be slots to auto-select from the moment the
          // panel opened — but that's a raw DOM write, which bypasses
          // flatpickr entirely: the visible altInput kept showing "Select
          // a date" while the real (hidden) input silently held a value.
          // renderDeliverySlots()'s auto-select then ran off THAT value,
          // saving a real Delivery-Date/Delivery-Time/_ZapietId to the
          // cart — so a customer could reach Checkout (which only checks
          // those cart attributes) without ever having picked a date
          // themselves. Slots must now wait for an actual date, via the
          // dateInput 'change' listener below or runPrefill()'s own
          // explicit dateInput._flatpickr.setDate(prefillDate, ...) call
          // (the one legitimate case of a date arriving without a click).
        }
        if (Array.isArray(data.disabled)) {
          const disabledDates = normalizeDisabledDates(data.disabled);
          deliverySchedule.disabled = disabledDates;
          // Blocks those dates in the calendar UI itself so a customer
          // can't click a Zapiet-excluded day (holiday, day off, etc.)
          // in the first place. renderDeliverySlotsForDate() below is
          // the second layer, for a disabled date that reaches
          // dateInput.value some other way (URL prefill, programmatic
          // setDate) without going through the picker's own click
          // handling.
          if (dateInput?._flatpickr) dateInput._flatpickr.set('disable', disabledDates);
        }
        if (Array.isArray(data.minDateSlots) && dateInput?.value === data.minDate) {
          await renderDeliverySlots(data.minDateSlots.map((s) => ({ id: s.id, from: s.available_from, until: s.available_until })));
          return;
        }
      } catch (e) {
        console.info('[CartDeliveryCalculator] Delivery calendar fallback is active.');
      }
      await renderDeliverySlotsForDate();
    }

    async function renderDeliverySlotsForDate(skipAutoSync) {
      const date = dateInput?.value;
      if (!date) return;
      // Second layer of enforcement for Zapiet's excluded dates (see the
      // flatpickr 'disable' set in initializeDeliveryCalendar()): if the
      // date already sitting in dateInput.value is one Zapiet blocked,
      // don't fall through to the generic weekly schedule as if it were
      // a normal business day — render no slots and surface the error.
      if ((deliverySchedule.disabled || []).includes(date)) {
        if (timeSelect) timeSelect.innerHTML = '<option value="">Choose a time</option>';
        if (errorEl) {
          errorEl.textContent = 'Delivery is not available on this date. Please choose another day.';
          errorEl.hidden = false;
        }
        return;
      }
      const weekday = new Intl.DateTimeFormat('en-US', { weekday: 'long' })
        .format(new Date(`${date}T12:00:00`))
        .toLowerCase();
      await renderDeliverySlots(deliverySchedule[weekday] || [], skipAutoSync);
    }

    async function renderDeliverySlots(slots, skipAutoSync) {
      if (!timeSelect) return;
      const leadTimeHours = window.LUCIOL_LEAD_TIME?.deliveryHours ?? 1;
      const available = filterSlotsByLeadTime(slots, dateInput?.value, leadTimeHours, 'from');
      timeSelect.innerHTML = '<option value="">Choose a time</option>';
      available.forEach((slot) => {
        const label = `${formatDisplayTime(slot.from)} - ${formatDisplayTime(slot.until)}`;
        const option = document.createElement('option');
        option.value = label;
        option.textContent = label;
        option.dataset.slotId = String(slot.id || '');
        option.dataset.startTime = slot.from;
        option.dataset.endTime = slot.until;
        timeSelect.appendChild(option);
      });
      // Auto-select the first slot so _ZapietId gets set right away
      // instead of waiting for the customer to manually open the
      // dropdown. Without this, a customer who picks a date and goes
      // straight to Checkout leaves _ZapietId empty/stale and Zapiet
      // silently falls back to whatever method it last saw — the root
      // cause of "pickup sometimes doesn't reach checkout". Only sync
      // when Delivery is the active tab: this function also runs as a
      // background pre-fetch when the Change panel first opens
      // (regardless of which method is showing), and we don't want that
      // to stomp on a Pickup selection.
      // skipAutoSync: applyPrefillFromUrl() passes true when it already
      // knows an explicit prefill_time is about to be applied right
      // after — syncing this placeholder selection first (a full
      // saveCartAttributes + per-line-item Zapiet sync) just to
      // immediately overwrite it with the real one was doubling that
      // round trip for no benefit.
      if (selectFirstAvailableOption(timeSelect) && method === 'delivery' && !skipAutoSync) {
        await handleDeliverySlotChange();
      }
    }

    async function validateDeliveryAddress(query) {
      if (!query) return;
      if (errorEl) errorEl.hidden = true;

      try {
        // Run the route-distance check and the components geocode
        // together — they're independent Google Maps calls, no reason to
        // pay for them one after another. A geocode failure (null) isn't
        // fatal here; it just means Delivery-Address1/City/... stay
        // whatever they were before instead of updating, and the address
        // still validates on distance/eligibility as normal.
        const [{ miles }, geocoded] = await Promise.all([
          computeDistanceMiles(storeAddress, query),
          geocodeAddress(query),
        ]);
        if (geocoded) lastAddressComponents = geocoded;
        const cart = await getCart();
        const rate = resolveRateByMiles(miles, getVisibleSubtotalCents(cart) / 100);
        lastRate = rate;

        if (!rate.eligible) {
          if (editPriceEl) editPriceEl.textContent = '—';
          if (errorEl) {
            errorEl.textContent = 'Delivery is not available for this address.';
            errorEl.hidden = false;
          }
          return;
        }

        saveToSession(query, lastAddressComponents);
        lastValidatedAddress = query;
        await saveCartAttributes({
          'Delivery-Address': query,
          'Delivery-Miles': miles.toFixed(1),
          'Estimated-Delivery-Rate': String(rate.price),
          'Delivery-Address1': lastAddressComponents?.address1 || '',
          'Delivery-City': lastAddressComponents?.city || '',
          'Delivery-Province': lastAddressComponents?.province || '',
          'Delivery-Zip': lastAddressComponents?.zip || '',
          'Delivery-Country': lastAddressComponents?.country || '',
          'Checkout-Method': 'delivery',
          // The actual mechanism that gets an address into checkout is
          // Zapiet's own native sync, not our sessionStorage/query-param
          // approach — it reads this exact attribute name off the cart.
          // getDeliverySelection() in product-delivery-selector.js
          // already sets it at add-to-cart time; this was the missing
          // piece on the cart page's own address flow (prefill links,
          // typing/selecting an address directly on /cart), which is
          // why a manually-entered product-page address reached checkout
          // and a cart-page one never did.
          'Delivery-Widget-Query': query,
        });
        // No-ops until a date/time is also picked (see the function's
        // own comment) — covers the case where address gets validated
        // AFTER date/time was already chosen (handleDeliverySlotChange()
        // covers the reverse order).
        await syncDeliverySelectionToLineItems();
        // The hidden "Home Delivery" fee line item (see
        // syncDeliveryFeeItem in product-delivery-selector.js) is synced
        // once at the Checkout button click instead of live here — see
        // that button's script in cart-drawer.liquid for why.
        await refreshBreakdown();
      } catch (err) {
        console.error('[CartDeliveryCalculator]', err);
        if (errorEl) {
          errorEl.textContent = 'We could not calculate delivery for this address.';
          errorEl.hidden = false;
        }
      }
    }

    // Zapiet's checkout script reads the DELIVERY ADDRESS off per-line-
    // item properties too — same as _ZapietId (see syncLineItemsZapietId's
    // own comment) — not off cart-level attributes. product-delivery-
    // selector.js already sends these as properties[_Delivery address /
    // _Delivery date / _Delivery time] in the add-to-cart form itself;
    // this is the cart page's equivalent, since there's no add-to-cart
    // form here to carry them on. Needs BOTH a validated address and a
    // picked date/time to mean anything, so it's called from wherever
    // either one changes (validateDeliveryAddress() and
    // handleDeliverySlotChange()) and simply no-ops until both exist.
    async function syncDeliverySelectionToLineItems() {
      const opt = timeSelect?.selectedOptions[0];
      const date = dateInput?.value;
      const time = timeSelect?.value;
      if (!date || !time || !opt?.dataset.slotId || !lastValidatedAddress) return;
      const zapietId = buildZapietId('D', deliveryLocationId, date, opt.dataset.startTime, opt.dataset.endTime);
      const feeLabel = lastRate?.eligible ? (lastRate.price === 0 ? 'Free' : `$${lastRate.price.toFixed(2)}`) : '';
      await syncLineItemsZapietId(zapietId, {
        '_Delivery address': lastValidatedAddress,
        '_Delivery date': formatCartDate(date),
        '_Delivery time': time,
        '_Estimated delivery fee': feeLabel,
      });
    }

    async function handleDeliverySlotChange() {
      const opt = timeSelect?.selectedOptions[0];
      const date = dateInput?.value;
      const time = timeSelect?.value;
      if (!date || !time || !opt?.dataset.slotId) return;
      await saveCartAttributes({
        'Delivery-Location-Id': String(deliveryLocationId),
        'Delivery-Date': formatCartDate(date),
        'Delivery-Time': time,
        'Delivery-Slot-Id': String(opt.dataset.slotId),
        'Checkout-Method': 'delivery',
        'Pickup-Location-Id': '',
        'Pickup-Date': '',
        'Pickup-Time': '',
      });
      if (lastValidatedAddress) {
        await syncDeliverySelectionToLineItems();
      } else {
        // Address not validated yet (e.g. customer picked date/time
        // before typing an address) — still push _ZapietId alone so
        // Zapiet's date/time selection is at least current; the address
        // properties get added on top the moment validateDeliveryAddress()
        // succeeds, via its own syncDeliverySelectionToLineItems() call.
        await syncLineItemsZapietId(
          buildZapietId('D', deliveryLocationId, date, opt.dataset.startTime, opt.dataset.endTime)
        );
      }
      await refreshBreakdown();
    }

    function initializeAddressAutocomplete() {
      if (!addressInput || !window.google?.maps?.places?.Autocomplete) return;
      const autocomplete = new google.maps.places.Autocomplete(addressInput, {
        fields: ['formatted_address', 'address_components'],
        types: ['address'],
      });
      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (place.formatted_address) {
          addressInput.value = place.formatted_address;
          lastAddressComponents = extractAddressComponents(place.address_components);
          validateDeliveryAddress(place.formatted_address);
        }
      });
    }

    // ---------------------------------------------------------------
    // Pickup: Zapiet locations + calendar
    // ---------------------------------------------------------------
    async function loadPickupLocations() {
      const u = new URL('https://api.zapiet.com/v1.0/pickup/locations');
      u.searchParams.set('shop', shopDomain);
      const shoppingCart = await getShoppingCartItems();
      const r = await fetch(u, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json; charset=UTF-8' },
        body: JSON.stringify({ geoSearchQuery: '', shoppingCart }),
      });
      if (!r.ok) throw new Error('Pickup locations failed');
      const d = await r.json();
      pickupLocations = Array.isArray(d.locations) ? d.locations : [];
    }

    async function loadPickupCalendar(id) {
      const u = new URL(`https://api.zapiet.com/v1.0/pickup/locations/${id}/calendar`);
      u.searchParams.set('shop', shopDomain);
      const shoppingCart = await getShoppingCartItems();
      const r = await fetch(u, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json; charset=UTF-8' },
        body: JSON.stringify({ shoppingCart }),
      });
      if (!r.ok) throw new Error('Pickup calendar failed');
      pickupCalendar = await r.json();
    }

    // Only one pickup location exists for this shop, so it's rendered as
    // a single plain line ("Company — address, city") instead of a
    // picker. `selectedPickupLocation` keeps the full object around so
    // handlePickupSlotChange() still has everything it needs to save.
    let selectedPickupLocation = null;

    function renderPickupLine(l) {
      selectedPickupLocation = l || null;
      if (!pickupLineEl) return;
      if (!l) {
        pickupLineEl.textContent = 'Pickup location unavailable';
        return;
      }
      const parts = [l.address_line_1, l.address_line_2, l.city, l.postal_code].filter(Boolean);
      pickupLineEl.textContent = [l.company_name, parts.join(', ')].filter(Boolean).join(' — ');
    }

    async function populatePickupSlotsSelect(slots, skipAutoSync) {
      if (!pickupTimeSelect) return;
      const leadTimeHours = window.LUCIOL_LEAD_TIME?.pickupHours ?? 2;
      const available = filterSlotsByLeadTime(slots, pickupDateInput?.value, leadTimeHours, 'available_from');
      pickupTimeSelect.innerHTML = '<option value="">Choose a pickup time</option>';
      available.forEach((s) => {
        const o = document.createElement('option');
        // Zapiet's real minDateSlots are genuine windows (from !== until);
        // the fallback defaultPickupSchedule() hours are single points in
        // time (from === until) — show those as one time, not "X - X".
        o.value = s.available_from === s.available_until
          ? formatDisplayTime(s.available_from)
          : `${formatDisplayTime(s.available_from)} - ${formatDisplayTime(s.available_until)}`;
        o.textContent = o.value;
        o.dataset.startTime = s.available_from;
        o.dataset.endTime = s.available_until;
        pickupTimeSelect.appendChild(o);
      });
      // Same reasoning as renderDeliverySlots(): auto-select the first
      // slot so _ZapietId is set immediately, guarded to only fire while
      // Pickup is the active tab. skipAutoSync: see renderDeliverySlots()
      // — same "an explicit prefill time is coming right after, don't
      // sync the throwaway placeholder first" reasoning.
      if (selectFirstAvailableOption(pickupTimeSelect) && method === 'pickup' && !skipAutoSync) {
        await handlePickupSlotChange();
      }
    }

    async function populatePickupCalendarUI(skipAutoSync) {
      if (!pickupDateInput || !pickupCalendar) return;
      // Same timezone fix as initializeDeliveryCalendar() — Zapiet's
      // pickup calendar response carries its own `timezone` too (same
      // value, but this location loads independently so it may resolve
      // first).
      if (pickupCalendar.timezone) {
        window.LUCIOL_LEAD_TIME = window.LUCIOL_LEAD_TIME || {};
        window.LUCIOL_LEAD_TIME.storeTimeZone = pickupCalendar.timezone;
      }
      // Zapiet's pickup calendar doesn't give a flat slot list like
      // delivery — it gives `daysOfWeek`, a min/max open-close window per
      // weekday (confirmed via a direct API call: 07:00-23:00 every day
      // for this location, NOT the 09:30-21:00 defaultPickupSchedule()
      // guessed). buildPickupScheduleFromDaysOfWeek() expands that into
      // real hourly slots — the live source of truth whenever present.
      if (pickupCalendar.daysOfWeek) {
        const built = buildPickupScheduleFromDaysOfWeek(pickupCalendar.daysOfWeek);
        if (built) Object.assign(pickupHourSchedule, built);
      }
      pickupDateInput.min = pickupCalendar.minDate || '';
      // No longer auto-writing pickupCalendar.minDate into
      // pickupDateInput.value here — same bug as the delivery calendar's
      // equivalent (see initializeDeliveryCalendar()): a raw DOM write
      // bypasses flatpickr, so the visible field kept showing "Select a
      // date" while a real date (and, via the auto-select below, a real
      // time) was already saved to the cart — letting Checkout through
      // with no pickup date the customer ever actually picked.
      if (pickupDateInput._flatpickr && pickupCalendar.minDate) {
        pickupDateInput._flatpickr.set('minDate', pickupCalendar.minDate);
      }
      // Same Zapiet-excluded-dates enforcement as the delivery calendar
      // (see initializeDeliveryCalendar()): block those days in the
      // picker UI itself. renderPickupSlotsForDate() is the second layer.
      if (Array.isArray(pickupCalendar.disabled)) {
        const disabledDates = normalizeDisabledDates(pickupCalendar.disabled);
        pickupHourSchedule.disabled = disabledDates;
        if (pickupDateInput._flatpickr) pickupDateInput._flatpickr.set('disable', disabledDates);
      }
      // Zapiet's pickup calendar only gives real, verified slots for
      // the single "nearest available" date (minDateSlots). Locking the
      // picker to just that day worked but was too restrictive — same
      // tradeoff delivery already makes: any later date falls back to
      // a generic weekly schedule (deliverySchedule, reused here) via
      // renderPickupSlotsForDate() instead of blocking selection. Both
      // now only run once the customer has actually picked a date (see
      // the pickupDateInput 'change' listener), so nothing here needs to
      // pre-render slots for a date no one has chosen yet.
    }

    async function renderPickupSlotsForDate(skipAutoSync) {
      const date = pickupDateInput?.value;
      if (!date) return;
      // Second layer of enforcement for Zapiet's excluded pickup dates
      // (see the flatpickr 'disable' set in populatePickupCalendarUI()).
      if ((pickupHourSchedule.disabled || []).includes(date)) {
        if (pickupTimeSelect) pickupTimeSelect.innerHTML = '<option value="">Choose a pickup time</option>';
        if (errorEl) {
          errorEl.textContent = 'Pickup is not available on this date. Please choose another day.';
          errorEl.hidden = false;
        }
        return;
      }
      const weekday = new Intl.DateTimeFormat('en-US', { weekday: 'long' })
        .format(new Date(`${date}T12:00:00`))
        .toLowerCase();
      // pickupHourSchedule slots are {from, until} (from === until, a
      // single hour mark); populatePickupSlotsSelect expects Zapiet's own
      // {available_from, available_until} shape.
      const slots = (pickupHourSchedule[weekday] || []).map((s) => ({
        available_from: s.from,
        available_until: s.until,
      }));
      await populatePickupSlotsSelect(slots, skipAutoSync);
    }

    // `pickupLocations.length` guards the one-time network fetch, but it
    // also meant switching Delivery → Pickup → Delivery → Pickup again
    // silently did nothing the second time (this function just returned
    // early) — leaving _ZapietId on its stale Delivery value. Splitting
    // the cached-render step (below, in the method-pill handler) from
    // this one-time load fixes that; see the "pickup" branch there.
    async function initializePickup() {
      if (pickupLocations.length) return;
      try {
        await loadPickupLocations();
        const first = pickupLocations[0];
        if (!first) throw new Error('No pickup locations');
        renderPickupLine(first);
        await loadPickupCalendar(first.id);
        await populatePickupCalendarUI();
      } catch (e) {
        console.error('[CartDeliveryCalculator] Pickup init failed', e);
        if (errorEl) {
          errorEl.textContent = 'Pickup availability could not be loaded.';
          errorEl.hidden = false;
        }
      }
    }

    async function handlePickupDateChange(skipAutoSync) {
      if (pickupDateInput.value === pickupCalendar?.minDate) {
        await populatePickupSlotsSelect(pickupCalendar.minDateSlots || [], skipAutoSync);
      } else {
        await renderPickupSlotsForDate(skipAutoSync);
      }
    }

    async function handlePickupSlotChange() {
      const l = selectedPickupLocation;
      const t = pickupTimeSelect?.selectedOptions[0];
      const date = pickupDateInput?.value;
      const time = pickupTimeSelect?.value;
      if (!l || !date || !time || !t?.dataset.startTime) return;
      // Mirror what validateDeliveryAddress() already does for Delivery:
      // stash the address in sessionStorage so cart-drawer.liquid's
      // checkout-button script can prefill checkout[shipping_address]
      // with it. Without this, Pickup wrote its address only to cart
      // ATTRIBUTES (for our own UI) — nothing ever reached the session
      // key the checkout redirect actually reads, so switching to
      // Pickup left checkout with no address at all.
      saveToSession(
        [l.address_line_1, l.address_line_2, l.city, l.postal_code].filter(Boolean).join(', '),
        {
          address1: l.address_line_1 || '',
          city: l.city || '',
          province: l.region || '',
          zip: l.postal_code || '',
          country: l.country || '',
        }
      );
      await saveCartAttributes({
        'Pickup-Location-Id': String(l.id),
        'Pickup-Date': formatCartDate(date),
        'Pickup-Time': time,
        'Pickup-Location-Company': l.company_name || '',
        'Pickup-Location-Address-Line-1': l.address_line_1 || '',
        'Pickup-Location-Address-Line-2': l.address_line_2 || '',
        'Pickup-Location-City': l.city || '',
        'Pickup-Location-Region': l.region || '',
        'Pickup-Location-Postal-Code': l.postal_code || '',
        'Pickup-Location-Country': l.country || '',
        'Pickup-Location-Phone': l.phone || '',
        'Checkout-Method': 'pickup',
        'Delivery-Location-Id': '',
        'Delivery-Date': '',
        'Delivery-Time': '',
        'Delivery-Slot-Id': '',
      });
      await syncLineItemsZapietId(buildZapietId('P', l.id, date, t.dataset.startTime, t.dataset.endTime));
      await refreshBreakdown();
    }

    // ---------------------------------------------------------------
    // Shipping method pills (inside the edit panel)
    // ---------------------------------------------------------------
    function setEditMethod(newMethod) {
      method = newMethod;
      methodButtons.forEach((b) => {
        const active = b.dataset.cartMethod === newMethod;
        b.classList.toggle('is-active', active);
        b.setAttribute('aria-pressed', String(active));
      });
      if (deliveryFieldsPanel) deliveryFieldsPanel.hidden = newMethod !== 'delivery';
      if (pickupFieldsPanel) pickupFieldsPanel.hidden = newMethod !== 'pickup';
    }

    // Extracted from the pill click handler so applyPrefillFromUrl() (see
    // bottom of init) can switch methods programmatically too, awaiting
    // the exact same sequence a real click would run instead of
    // duplicating it.
    async function switchToMethod(newMethod) {
      setEditMethod(newMethod);
      if (newMethod === 'pickup') {
        await saveCartAttributes({
          'Checkout-Method': 'pickup',
          'Delivery-Location-Id': '',
          'Delivery-Date': '',
          'Delivery-Time': '',
          'Delivery-Slot-Id': '',
        });
        // initializePickup() only does the actual network fetch once
        // (see its own comment) — populatePickupCalendarUI() re-renders
        // from the already-cached pickupCalendar every time, which is
        // what re-selects a slot and re-syncs _ZapietId to Pickup on
        // every switch, not just the first.
        await initializePickup();
        if (pickupCalendar) await populatePickupCalendarUI();
      } else {
        await saveCartAttributes({ 'Checkout-Method': 'delivery' });
        // changeBtn now only preloads whichever panel was already active
        // when Change was opened (see its comment) — if that was Pickup,
        // Delivery's real Zapiet calendar (minDate/disabled dates) was
        // never fetched, so do it now, once, before re-rendering slots.
        if (!deliveryCalendarLoaded) {
          deliveryCalendarLoaded = true;
          await initializeDeliveryCalendar();
        }
        await renderDeliverySlotsForDate();
      }
      await refreshBreakdown();
    }

    methodButtons.forEach((btn) => {
      btn.addEventListener('click', () => switchToMethod(btn.dataset.cartMethod));
    });

    // ---------------------------------------------------------------
    // Change / Back
    // ---------------------------------------------------------------
    // Named (not an inline arrow in addEventListener) so
    // applyPrefillFromUrl() can `await openEditPanel()` directly instead
    // of firing a synthetic .click() and polling for completion.
    async function openEditPanel() {
      if (!editPanel) return;
      editPanel.hidden = false;
      if (recapRow) recapRow.hidden = true;

      if (!editPanelReady) {
        editPanelReady = true;

        // Google Maps loading and the Zapiet network calls below used to
        // run strictly one after another, adding their latencies
        // together — but they're unrelated: only
        // initializeAddressAutocomplete() (and later, typing an address)
        // actually needs Maps loaded, and the Zapiet calendar/pickup
        // calls don't touch it at all. Kick both off together and only
        // block on Maps right before the call that actually needs it.
        const mapsReady = loadGoogleMapsReady();
        initDatePickers();

        // getCart() doesn't depend on either panel's network calls below —
        // fire it in parallel instead of waiting for those to finish first.
        const cartPromise = getCart();

        // Only load the panel that's actually visible right now (matches
        // the customer's current Checkout-Method). The other one still
        // loads lazily the first time they click its pill (see the
        // method-pill handler above) — no need to pay for both Zapiet
        // round trips (delivery calendar + pickup locations/calendar) up
        // front when only one panel is shown.
        const calendarReady = method === 'pickup'
          ? // initializePickup() already renders the slot list itself
            // (populatePickupCalendarUI is its last step) — no need to
            // call it again after, that was just re-doing the same
            // network+DOM work a second time.
            initializePickup()
          : (async () => {
              await renderDeliverySlotsForDate();
              await initializeDeliveryCalendar();
            })();

        await mapsReady;
        initializeAddressAutocomplete();

        await calendarReady;
        deliveryCalendarLoaded = method !== 'pickup';

        const cart = await cartPromise;
        const attrs = cart.attributes || {};
        const savedAddress = attrs['Delivery-Address'] || readFromSession()?.address;
        if (savedAddress && addressInput) addressInput.value = savedAddress;
        // Only treat it as "validated" if it actually came from the cart's
        // own Delivery-Address attribute (i.e. a real rate was already
        // saved for it) — a sessionStorage-only address (e.g. saved from
        // the product page for a different delivery zone/date) hasn't
        // been priced for THIS cart yet, so it must still go through
        // validateDeliveryAddress() before checkout is allowed.
        lastValidatedAddress = attrs['Delivery-Address'] || '';

        // Both panels above default to auto-selecting the FIRST available
        // slot (see the two-fixes comment on selectFirstAvailableOption).
        // If the customer had already picked a specific date/time earlier
        // (product page or a previous cart edit), restore that instead —
        // but only touch anything if it actually differs from what got
        // auto-selected, so the common case (saved slot == earliest slot,
        // since that's what gets auto-picked everywhere else too) doesn't
        // pay for an extra, redundant save + _ZapietId sync round trip.
        if (method === 'delivery') {
          const savedDeliveryDate = attrs['Delivery-Date'];
          const savedDeliveryTime = attrs['Delivery-Time'];
          if (savedDeliveryDate && dateInput) {
            const isoDate = savedDeliveryDate.replaceAll('/', '-');
            if (dateInput.value !== isoDate) {
              if (dateInput._flatpickr) dateInput._flatpickr.setDate(isoDate, false);
              else dateInput.value = isoDate;
              await renderDeliverySlotsForDate();
            }
          }
          if (savedDeliveryTime && timeSelect && timeSelect.value !== savedDeliveryTime) {
            const match = Array.from(timeSelect.options).find((o) => o.value === savedDeliveryTime);
            if (match) {
              timeSelect.value = savedDeliveryTime;
              await handleDeliverySlotChange();
            }
          }
        } else if (method === 'pickup') {
          const savedPickupDate = attrs['Pickup-Date'];
          const savedPickupTime = attrs['Pickup-Time'];
          if (savedPickupDate && pickupDateInput) {
            const isoDate = savedPickupDate.replaceAll('/', '-');
            if (pickupDateInput.value !== isoDate) {
              if (pickupDateInput._flatpickr) pickupDateInput._flatpickr.setDate(isoDate, false);
              else pickupDateInput.value = isoDate;
              await handlePickupDateChange();
            }
          }
          if (savedPickupTime && pickupTimeSelect && pickupTimeSelect.value !== savedPickupTime) {
            const match = Array.from(pickupTimeSelect.options).find((o) => o.value === savedPickupTime);
            if (match) {
              pickupTimeSelect.value = savedPickupTime;
              await handlePickupSlotChange();
            }
          }
        }
      }
    }
    changeBtn?.addEventListener('click', openEditPanel);

    backBtn?.addEventListener('click', () => {
      if (!editPanel) return;
      editPanel.hidden = true;
      if (recapRow) recapRow.hidden = false;
      refreshBreakdown();
    });

    dateInput?.addEventListener('change', () => renderDeliverySlotsForDate());
    timeSelect?.addEventListener('change', () => handleDeliverySlotChange());
    addressInput?.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        clearTimeout(addressInvalidateTimer);
        validateDeliveryAddress(addressInput.value.trim());
      }
    });
    // Autocomplete's place_changed listener and the Enter-key handler
    // above are the only two ways validateDeliveryAddress() used to run —
    // a customer who types an address and then just taps/clicks
    // elsewhere (very common on mobile, where there's no separate
    // "confirm" step) never triggered either one, so Estimated-Delivery-
    // Rate/Delivery-Address never got saved and checkout proceeded with
    // no real address on file. `blur` is the fallback for that case.
    addressInput?.addEventListener('blur', () => {
      clearTimeout(addressInvalidateTimer);
      const val = addressInput.value.trim();
      if (val && val !== lastValidatedAddress) validateDeliveryAddress(val);
    });
    // Typing over an already-validated address must not leave the old
    // rate/attributes (and the checkout gate below, which reads them)
    // thinking a valid address is still on file. Debounced so normal
    // typing speed doesn't fire a cart/update.js call per keystroke.
    addressInput?.addEventListener('input', () => {
      if (errorEl) errorEl.hidden = true;
      const val = addressInput.value.trim();
      if (val === lastValidatedAddress) return;
      lastRate = null;
      if (priceEl) priceEl.textContent = 'Enter address above';
      if (editPriceEl) editPriceEl.textContent = '—';
      if (orderTotalEl) {
        getCart().then((cart) => {
          orderTotalEl.textContent = money(getVisibleSubtotalCents(cart), currentCurrency(cart));
        });
      }
      clearTimeout(addressInvalidateTimer);
      addressInvalidateTimer = setTimeout(() => {
        saveCartAttributes({ 'Estimated-Delivery-Rate': '', 'Delivery-Address': '', 'Delivery-Widget-Query': '' });
      }, 400);
    });
    pickupDateInput?.addEventListener('change', () => handlePickupDateChange());
    pickupTimeSelect?.addEventListener('change', () => handlePickupSlotChange());

    // ---------------------------------------------------------------
    // Initial recap render — source of truth is whatever is already on
    // the cart, written by product-delivery-selector.js.
    // ---------------------------------------------------------------
    await refreshBreakdown();

    // The /cart page's own instance (see main-cart.liquid) shows the
    // Delivery Details card open at all times — no "Change" link to
    // click, since the recap/breakdown block is hidden there entirely.
    // Run the same one-time bootstrap a click would (Google Maps, Zapiet
    // calendars, restoring any already-saved date/time) right away
    // instead of waiting for an interaction that never happens on this
    // instance. The drawer's own instance is untouched — it has no
    // [data-cart-page-delivery] ancestor, so this never fires there.
    if (root.closest('[data-cart-page-delivery]')) {
      await openEditPanel();
    }

    // ---------------------------------------------------------------
    // URL prefill — for phone/manual orders. A staff member sends the
    // customer a normal cart permalink (adds the product like any link)
    // with extra query params:
    //   ?prefill_method=delivery|pickup   (optional, defaults to delivery)
    //   &prefill_address=123+Main+St...   (delivery only)
    //   &prefill_date=2026-08-25          (YYYY-MM-DD, either method)
    //   &prefill_time=11:00 AM            (matched against the rendered
    //                                       slot options — falls back to
    //                                       whatever auto-selects if no
    //                                       exact match is found)
    // The customer still opens the cart and lands on the SAME "Change"
    // panel/fields they'd fill in themselves — this just pre-types the
    // values through the exact same functions a real interaction calls
    // (openEditPanel, validateDeliveryAddress, handleDeliverySlotChange,
    // etc.), so pricing/_ZapietId/cart attributes end up identical to a
    // customer typing it all in by hand. Nothing here bypasses that
    // logic — it only saves the customer some typing.
    // ---------------------------------------------------------------
    await applyPrefillFromUrl();

    async function applyPrefillFromUrl() {
      let params;
      try {
        params = new URLSearchParams(window.location.search);
      } catch {
        return;
      }
      const prefillAddress = params.get('prefill_address');
      const prefillDate = params.get('prefill_date');
      const prefillTime = params.get('prefill_time');
      const prefillMethod = params.get('prefill_method');
      if (!prefillAddress && !prefillDate && !prefillTime && !prefillMethod) return;

      // The fields visibly changing one at a time (method pill, address,
      // date, time) while this runs reads as broken/laggy even though
      // it only takes a couple seconds — a simple overlay over the
      // fields that are about to change reads as "working" instead.
      // Always removed in the finally block below, even on error.
      showPrefillOverlay();
      try {
        await runPrefill(prefillAddress, prefillDate, prefillTime, prefillMethod);
      } finally {
        hidePrefillOverlay();
      }
    }

    function showPrefillOverlay() {
      if (!editPanel || editPanel.querySelector('[data-prefill-overlay]')) return;
      const overlay = document.createElement('div');
      overlay.setAttribute('data-prefill-overlay', '');
      overlay.className = 'cart-summary__prefill-overlay';
      // Inline as a fallback in case this JS deploys ahead of the CSS in
      // cart-delivery-calculator.liquid (separate file, separate push) —
      // without this the overlay div would render as an unstyled sliver
      // of text instead of an actual overlay.
      overlay.style.cssText =
        'position:absolute;inset:0;z-index:5;display:flex;flex-direction:column;' +
        'align-items:center;justify-content:center;gap:12px;background:rgba(246,250,249,0.94);' +
        'border-radius:10px;text-align:center;padding:24px;';
      overlay.innerHTML =
        '<span class="cart-summary__prefill-spinner" aria-hidden="true" style="width:26px;height:26px;border:3px solid rgba(14,62,49,0.2);border-top-color:#0e3e31;border-radius:50%;animation:cart-summary-prefill-spin 0.8s linear infinite;"></span>' +
        '<span class="cart-summary__prefill-text" style="font-family:Poppins,sans-serif;font-weight:600;font-size:12px;color:#0e3e31;">Preparing your order…</span>';
      if (editPanel.style.position !== 'relative' && editPanel.style.position !== 'absolute') {
        editPanel.style.position = editPanel.style.position || 'relative';
      }
      editPanel.appendChild(overlay);
    }

    function hidePrefillOverlay() {
      editPanel?.querySelector('[data-prefill-overlay]')?.remove();
    }

    async function runPrefill(prefillAddress, prefillDate, prefillTime, prefillMethod) {

      // The drawer itself starts closed (hidden attribute) regardless of
      // which page the customer landed on via the permalink — open it so
      // the prefilled Change panel is actually visible, not just filled
      // in behind a closed drawer. Skipped when a [data-cart-page-delivery]
      // instance exists on this page (i.e. we're on /cart itself, see
      // main-cart.liquid) — that instance already shows the same fields
      // open directly on the page, so popping the drawer on top of it
      // would just cover up the page with a redundant copy of itself.
      if (!document.querySelector('[data-cart-page-delivery]')) {
        document.querySelector('cart-drawer')?.show?.();
      }

      // Runs the exact one-time bootstrap a real click on "Change" would
      // (Google Maps, Zapiet calendars, restoring any already-saved
      // date/time) before anything below touches the fields.
      await openEditPanel();

      const wantsPickup = prefillMethod === 'pickup';
      if (wantsPickup && method !== 'pickup') {
        await switchToMethod('pickup');
      } else if (!wantsPickup && method !== 'delivery') {
        await switchToMethod('delivery');
      }

      function findSlotOption(select, wantedTime) {
        if (!select || !wantedTime) return null;
        return (
          Array.from(select.options).find(
            (o) => o.value === wantedTime || o.dataset.startTime === wantedTime
          ) ||
          // Loose match — lets "11:00 AM" match a rendered range label
          // like "11:00 AM - 5:00 PM" without needing the exact string.
          Array.from(select.options).find((o) => o.value.includes(wantedTime)) ||
          null
        );
      }

      if (!wantsPickup) {
        if (prefillAddress && addressInput) {
          addressInput.value = prefillAddress;
          await validateDeliveryAddress(prefillAddress);
        }
        let slotSynced = false;
        if (prefillDate && dateInput) {
          if (dateInput._flatpickr) dateInput._flatpickr.setDate(prefillDate, false);
          else dateInput.value = prefillDate;
          // Renders slots but skips the auto-select's own sync round trip
          // when an explicit prefillTime is given below — that slot is
          // about to get set for real a few lines down, so syncing the
          // placeholder first would just be a full extra saveCartAttributes
          // + per-line-item Zapiet sync that gets immediately overwritten.
          await renderDeliverySlotsForDate(Boolean(prefillTime));
        }
        if (prefillTime && timeSelect) {
          const match = findSlotOption(timeSelect, prefillTime);
          if (match) {
            if (timeSelect.value !== match.value) timeSelect.value = match.value;
            await handleDeliverySlotChange();
            slotSynced = true;
          }
        }
        // No exact prefillTime match (or none given) and the auto-select
        // sync was skipped above — fall back to the first available slot
        // now so _ZapietId still ends up set instead of staying empty.
        if (prefillDate && !slotSynced && timeSelect && !timeSelect.value) {
          if (selectFirstAvailableOption(timeSelect)) await handleDeliverySlotChange();
        }
      } else {
        let pickupSlotSynced = false;
        if (prefillDate && pickupDateInput) {
          if (pickupDateInput._flatpickr) pickupDateInput._flatpickr.setDate(prefillDate, false);
          else pickupDateInput.value = prefillDate;
          await handlePickupDateChange(Boolean(prefillTime));
        }
        if (prefillTime && pickupTimeSelect) {
          const match = findSlotOption(pickupTimeSelect, prefillTime);
          if (match) {
            if (pickupTimeSelect.value !== match.value) pickupTimeSelect.value = match.value;
            await handlePickupSlotChange();
            pickupSlotSynced = true;
          }
        }
        if (prefillDate && !pickupSlotSynced && pickupTimeSelect && !pickupTimeSelect.value) {
          if (selectFirstAvailableOption(pickupTimeSelect)) await handlePickupSlotChange();
        }
      }

      await refreshBreakdown();
    }
  }

  function initAllUninitialized(scope) {
    scope.querySelectorAll(ROOT_SELECTOR).forEach((el) => {
      if (el.dataset.initialized) return;
      el.dataset.initialized = 'true';
      init(el);
    });
  }

  document.addEventListener('DOMContentLoaded', () => initAllUninitialized(document));

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.addedNodes.length) {
        initAllUninitialized(document);
        break;
      }
    }
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });

  if (document.readyState !== 'loading') {
    initAllUninitialized(document);
  }
})();
